import { UI, VStack, HStack, Stack } from '@genial/components';
import { Color, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Box, Skeleton, ISkeletonProps, Pressable, IconButton } from 'native-base';
import React, { useState } from 'react';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { orderSequenceStatusQuery } from '../../../../store/orderHistory/selector';
import { getValidatedStatusIcon } from '../../../../utils/getValidatedStatusIcon';

import { tooltipText } from './constants';

const ColoredSkeleton = (props: ISkeletonProps) => (
  <Skeleton {...props} endColor={Color['brand.primary.lightest']} />
);

export interface StatusesSequenceProps {
  isMobile?: boolean;
}

export const OrderStatusesSequence: React.FC<StatusesSequenceProps> = ({ isMobile }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const { loading, data, error } = useRecoilQuery(orderSequenceStatusQuery);

  const statuses = data?.data?.map((status) => ({ ...status, ...getValidatedStatusIcon(status) }));

  const showLoading = loading || error || !statuses;

  const handlePress = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  return (
    <UI.Shadow level={1} testId="status-sequence-shadow" borderRadius="8px">
      <VStack py={Spacing['stack.xxxs']} px={Spacing['inline.xxs']} space={Spacing['stack.nano']}>
        <HStack justifyContent="space-between">
          <HStack space={Spacing['inline.quarck']} alignItems="center">
            <UI.Text variant="semibold" color="neutral.dark">
              Entenda a sequência dos status
            </UI.Text>
            <UI.Tooltip label={tooltipText} testId="status-tooltip">
              <Pressable>
                <UI.Icon
                  testId="status-tooltip-icon"
                  name="Info"
                  size="xxs"
                  color={Color['brand.tertiary.principal']}
                />
              </Pressable>
            </UI.Tooltip>
          </HStack>
          {!!isMobile && (
            <IconButton
              p={0}
              variant="unstyled"
              onPress={handlePress}
              icon={
                <UI.Icon
                  name={isMobileOpen ? 'ChevronUp' : 'ChevronDown'}
                  size="xxs"
                  color={Color['neutral.black']}
                />
              }
            />
          )}
        </HStack>
        {(!isMobile || (!!isMobile && isMobileOpen)) && (
          <Stack>
            <Box
              id="status-sequence-line"
              position="absolute"
              borderColor={Color['neutral.medium']}
              borderStyle="dashed"
              {...(!isMobile && {
                w: 'full',
                top: '50%',
                borderBottomWidth: '1px',
              })}
              {...(isMobile && {
                left: '12px',
                borderLeftWidth: '1px',
                top: 0,
                bottom: 0,
              })}
            />
            <Stack
              justifyContent="space-between"
              position="relative"
              space={isMobile ? Spacing['stack.nano'] : 0}
              direction={isMobile ? 'column' : 'row'}>
              {showLoading
                ? Array.from({ length: 5 }).map((_, index) => (
                    <ColoredSkeleton key={index.toString()} width="12%" h="14px" rounded="full" />
                  ))
                : statuses.map((status, index) => (
                    <HStack
                      key={status.statusSequenceNumber.toString()}
                      alignItems="center"
                      space={Spacing['inline.quarck']}
                      backgroundColor={Color['neutral.white']}
                      px={Utils.Responsive.TabletDesktop({
                        desktop: Spacing['inline.xxxs'],
                        tablet: Spacing['inline.quarck'],
                      })}
                      {...{
                        ...((index === 0 || isMobile) && { pl: 0 }),
                        ...(index === statuses.length - 1 && { pr: 0 }),
                      }}>
                      <UI.IconStatus
                        testId={`status-sequence-icon-${index}`}
                        bgColor={status.backgroundColor}
                        size={status.iconSize}
                        color={status.iconColor}
                        icon={status.icon}
                      />
                      <UI.Text variant="regular" color="neutral.dark" textTransform="uppercase">
                        {status.statusName}
                      </UI.Text>
                    </HStack>
                  ))}
            </Stack>
          </Stack>
        )}
      </VStack>
    </UI.Shadow>
  );
};
